import { Group, SegmentedControl, Stack, Text, useComputedColorScheme, useMantineColorScheme } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import type { Locale } from '@tnn/core'
import type { Density } from '@tnn/ui'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import { PushSetting } from '../pwa/push'

export interface Preferences {
  locale?: Locale
  colorScheme?: 'light' | 'dark' | 'auto'
  density?: Density
  fontScale?: number
}

interface PreferencesValue {
  prefs: Required<Preferences>
  setPreference: <K extends keyof Preferences>(key: K, value: Required<Preferences>[K]) => void
  /** Adopt what the server stored for this user (other device) */
  adopt: (prefs: Preferences) => void
}

const PreferencesContext = createContext<PreferencesValue | null>(null)

const defaults: Required<Preferences> = { locale: 'th', colorScheme: 'auto', density: 'comfortable', fontScale: 1 }
const STORAGE_KEY = 'tnn.prefs'

/** What this browser saved, read before React starts (e.g. the starting language). */
export function storedPreferences(): Preferences {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Preferences
  } catch {
    return {}
  }
}

/**
 * Personal settings for long working days: language, light/dark, table
 * density and text size. Kept in the browser and synced to the user's
 * account so every device and every app looks the same.
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const [stored, setStored] = useLocalStorage<Preferences>({ key: STORAGE_KEY, defaultValue: {}, getInitialValueInEffect: false })
  const prefs = useMemo<Required<Preferences>>(() => ({ ...defaults, ...stored }), [stored])

  useEffect(() => {
    document.documentElement.dataset.density = prefs.density
  }, [prefs.density])

  useEffect(() => {
    if (!i18n.language?.startsWith(prefs.locale)) i18n.changeLanguage(prefs.locale)
  }, [prefs.locale, i18n])

  const setPreference = useCallback<PreferencesValue['setPreference']>(
    (key, value) => {
      setStored((s) => ({ ...s, [key]: value }))
      api.put('/me/preferences', { [key]: value }).catch(() => undefined)
    },
    [setStored],
  )

  const adopt = useCallback((server: Preferences) => setStored((s) => ({ ...s, ...server })), [setStored])

  const value = useMemo(() => ({ prefs, setPreference, adopt }), [prefs, setPreference, adopt])
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
}

export function usePreferences(): PreferencesValue {
  const value = useContext(PreferencesContext)
  if (!value) throw new Error('usePreferences must be used inside <PreferencesProvider>')
  return value
}

/**
 * Applies the colour-scheme preference inside Mantine, and tints the browser's
 * bar (an installed app's status bar) like the app's top bar, light or dark.
 */
export function ColorSchemeSync() {
  const { prefs } = usePreferences()
  const { setColorScheme } = useMantineColorScheme()
  const scheme = useComputedColorScheme('light')
  useEffect(() => setColorScheme(prefs.colorScheme), [prefs.colorScheme, setColorScheme])
  // biome-ignore lint/correctness/useExhaustiveDependencies: read the page's colour again whenever the scheme changes
  useEffect(() => {
    // After Mantine has switched the page's colours
    const frame = requestAnimationFrame(() => {
      const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      if (meta) meta.content = getComputedStyle(document.body).backgroundColor
    })
    return () => cancelAnimationFrame(frame)
  }, [scheme])
  return null
}

/** The controls shown in the account menu's preferences dialog. */
export function PreferencesForm() {
  const { t } = useTranslation()
  const { prefs, setPreference } = usePreferences()
  const row = (label: string, control: ReactNode) => (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="sm">{label}</Text>
      {control}
    </Group>
  )
  return (
    <Stack gap="lg">
      {row(
        t('prefs.language'),
        <SegmentedControl
          value={prefs.locale}
          onChange={(v) => setPreference('locale', v as Locale)}
          data={[
            { value: 'th', label: 'ไทย' },
            { value: 'en', label: 'English' },
          ]}
        />,
      )}
      {row(
        t('prefs.theme'),
        <SegmentedControl
          value={prefs.colorScheme}
          onChange={(v) => setPreference('colorScheme', v as Preferences['colorScheme'] & string)}
          data={[
            { value: 'light', label: t('prefs.light') },
            { value: 'dark', label: t('prefs.dark') },
            { value: 'auto', label: t('prefs.auto') },
          ]}
        />,
      )}
      {row(
        t('prefs.density'),
        <SegmentedControl
          value={prefs.density}
          onChange={(v) => setPreference('density', v as Density)}
          data={[
            { value: 'comfortable', label: t('prefs.comfortable') },
            { value: 'compact', label: t('prefs.compact') },
          ]}
        />,
      )}
      {row(
        t('prefs.textSize'),
        <SegmentedControl
          value={String(prefs.fontScale)}
          onChange={(v) => setPreference('fontScale', Number(v))}
          data={[
            { value: '0.92', label: t('prefs.small') },
            { value: '1', label: t('prefs.medium') },
            { value: '1.1', label: t('prefs.large') },
          ]}
        />,
      )}
      {/* Per device and per app, not synced like the rows above */}
      <PushSetting />
    </Stack>
  )
}
